import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface MonthlyAggregate {
  yearMonth: string;
  callCount: number;
  totalDurationSec: number;
  totalMinutes: number;
  estimatedCostUsd: number;
  breakdown: {
    telephonyUsd: number;
    llmUsd: number;
    ttsUsd: number;
    sttUsd: number;
  };
  avgLatencyP50Ms: number;
  avgLatencyP95Ms: number;
  topModel: string | null;
  bookedCalls: number;
  computedAtMs: number;
}

export interface UsageOverviewItem {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'user';
  membership: {
    tier?: string;
    status?: string;
  } | null;
  usage: MonthlyAggregate;
}

export interface UsageOverview {
  yearMonth: string;
  year: number;
  month: number;
  isCurrentMonth: boolean;
  ratesEffectiveDate: string;
  items: UsageOverviewItem[];
  totals: {
    callCount: number;
    totalMinutes: number;
    estimatedCostUsd: number;
    breakdown: {
      telephonyUsd: number;
      llmUsd: number;
      ttsUsd: number;
      sttUsd: number;
    };
  };
}

export interface UserUsageDetail {
  user: {
    uid: string;
    email: string;
    displayName: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'user';
    membership: { tier?: string; status?: string } | null;
    disabled: boolean;
    authLastSignIn: string | null;
  };
  ratesEffectiveDate: string;
  history: MonthlyAggregate[];
  recent: Array<{
    id: string;
    startedAt: string | null;
    endedAt: string | null;
    durationSec: number;
    callerNumber: string | null;
    provider: string | null;
    outcome: string | null;
    latencyP50: number;
    latencyP95: number;
    llmProviderUsed: string | null;
    llmModelUsed: string | null;
    llmTokensIn: number;
    llmTokensOut: number;
    cost: {
      telephonyUsd: number;
      llmUsd: number;
      ttsUsd: number;
      sttUsd: number;
      totalUsd: number;
      meta: {
        matchedLlmModel: string | null;
        matchedTtsModel: string;
        matchedSttProvider: string;
        durationSec: number;
        assistantChars: number;
        tokensIn: number;
        tokensOut: number;
      };
    };
  }>;
}

export interface CostRates {
  effectiveDate: string;
  telephonyUsdPerMinute: { twilio: number; telnyx: number };
  ttsUsdPer1kChars: {
    elevenlabs_multilingual_v2: number;
    elevenlabs_flash_v2_5: number;
    elevenlabs_default: number;
    openai_default: number;
    google_default: number;
  };
  sttUsdPerMinute: {
    deepgram: number;
    elevenlabs: number;
    openai: number;
    default: number;
  };
  llm: Array<{
    provider: string;
    model: string;
    inputUsdPerMTok: number;
    outputUsdPerMTok: number;
  }>;
}

export function useUsageOverview(year: number, month: number) {
  const { isAdmin } = useUser();
  return useQuery<UsageOverview>({
    queryKey: ['admin-usage', 'overview', year, month] as const,
    enabled: isAdmin,
    staleTime: 60_000,
    queryFn: async () => {
      const fn = httpsCallable<{ year: number; month: number }, UsageOverview>(
        functions,
        'adminGetUsageOverview',
      );
      const r = await fn({ year, month });
      return r.data;
    },
  });
}

/**
 * Self-service: returns the *signed-in user's own* usage detail. Pinned
 * to `request.auth.uid` server-side, so a non-admin cannot pass another
 * uid. Same payload shape as `useUserUsage` so the page component is
 * identical.
 */
export function useMyUsage() {
  const { user } = useUser();
  return useQuery<UserUsageDetail>({
    queryKey: ['my-usage', user?.uid] as const,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<
        { monthsBack?: number; recentLimit?: number },
        UserUsageDetail
      >(functions, 'getMyUsage');
      const r = await fn({ monthsBack: 12, recentLimit: 50 });
      return r.data;
    },
  });
}

export function useUserUsage(uid: string | undefined) {
  const { isAdmin } = useUser();
  return useQuery<UserUsageDetail>({
    queryKey: ['admin-usage', 'user', uid] as const,
    enabled: isAdmin && !!uid,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<
        { uid: string; monthsBack?: number; recentLimit?: number },
        UserUsageDetail
      >(functions, 'adminGetUserUsage');
      const r = await fn({ uid: uid!, monthsBack: 12, recentLimit: 50 });
      return r.data;
    },
  });
}

export function useCostRates() {
  const { isAdmin } = useUser();
  return useQuery<{ rates: CostRates }>({
    queryKey: ['admin-usage', 'rates'] as const,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { rates: CostRates }>(
        functions,
        'adminGetCostRates',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useUpdateCostRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rates: CostRates) => {
      const fn = httpsCallable<{ rates: CostRates }, { ok: true }>(
        functions,
        'adminUpdateCostRates',
      );
      const r = await fn({ rates });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-usage'] });
    },
  });
}
